// ─────────────────────────────────────────────────────────────────────────────
// Contrato do CÉREBRO do Hexis — POST /hexis/chat. A doutrina diz "o mesmo cérebro,
// perspetivas diferentes": o FAB do lean e o da consola HQ têm UIs distintas (uma
// abre página, a outra um painel), mas o CONTRATO é o mesmo. Antes: o lean tinha
// `askHexis` e a HQ hardcodava `{message,user_id,context}` inline → drift à espera.
// Agora a forma do pedido/resposta vive AQUI, uma vez, injetando o http de cada app.
// ─────────────────────────────────────────────────────────────────────────────

export const HEXIS_CHAT_PATH = '/hexis/chat';
export const HEXIS_TOOL_APPROVE_PATH = '/hexis/tool/approve';

// Contexto = a PERSPETIVA (é isto que separa as responsabilidades sem separar o
// cérebro): surface/role na HQ (staff/operação), tab/subtab no lean (onde estou).
export interface HexisContext {
  surface?: string;
  role?: string;
  tab?: string;
  subtab?: string;
  label?: string;
}
export interface HexisChatRequest {
  message: string;
  userId: string;
  eventId?: string;
  context?: HexisContext;
}
// Ação PROPOSTA pelo Hexis a aguardar aprovação humana (Human-in-the-Loop). O
// `token` é uma proposta assinada (HMAC) pelo servidor — o cliente devolve-o tal
// e qual ao aprovar; nunca o constrói nem o adultera.
export interface HexisPendingAction {
  token: string;
  tool: string;
  summary: string;
}
export interface HexisReply {
  reply?: string | null;
  suggestions?: string[] | null;
  intent?: string | null;
  pending_action?: HexisPendingAction | null;
}

// Corpo canónico do POST /hexis/chat — a MESMA forma nas duas superfícies.
export function buildHexisChatBody(req: HexisChatRequest): Record<string, unknown> {
  return {
    message: req.message,
    user_id: req.userId,
    ...(req.eventId ? { event_id: req.eventId } : {}),
    ...(req.context ? { context: req.context } : {}),
  };
}

// Normaliza a resposta (tolerante) — no máx. 3 sugestões, como as duas UIs esperam.
export function normalizeHexisReply(raw: unknown): HexisReply {
  const r = (raw ?? {}) as HexisReply;
  const pa = r.pending_action;
  return {
    reply: r.reply ?? null,
    suggestions: Array.isArray(r.suggestions) ? r.suggestions.slice(0, 3) : null,
    intent: r.intent ?? null,
    pending_action: pa && typeof pa.token === 'string' && typeof pa.tool === 'string'
      ? { token: pa.token, tool: pa.tool, summary: String(pa.summary ?? '') }
      : null,
  };
}

// Resultado da aprovação de uma ação (o que a rota /hexis/tool/approve devolve).
export interface HexisApproveResult {
  ok: boolean;
  result?: unknown;
  error?: string;
}

/** Corpo canónico do POST /hexis/tool/approve. */
export function buildHexisApproveBody(token: string, userId: string): Record<string, unknown> {
  return { token, user_id: userId };
}

/**
 * Cliente do chat com INJEÇÃO do `post` (cada app traz o seu http — o lean o
 * `@/services/http`, a HQ o `@/api/http`). Framework-agnóstico de propósito.
 */
export function createHexisChat(post: (path: string, body: unknown) => Promise<unknown>) {
  return async (req: HexisChatRequest): Promise<HexisReply> =>
    normalizeHexisReply(await post(HEXIS_CHAT_PATH, buildHexisChatBody(req)));
}

/** Aprovação de uma ação proposta — devolve o token assinado ao servidor. */
export function createHexisApprove(post: (path: string, body: unknown) => Promise<unknown>) {
  return async (token: string, userId: string): Promise<HexisApproveResult> =>
    (await post(HEXIS_TOOL_APPROVE_PATH, buildHexisApproveBody(token, userId))) as HexisApproveResult;
}

// ─────────────────────────────────────────────────────────────────────────────
// STREAMING — o raciocínio do Hexis em tempo real (POST /hexis/ai/chat, SSE).
//
// Porque é que isto vive aqui e não em cada app: o servidor emite eventos
// `{type:'step'|'token'|'done'|'error'}`, e traduzir esse fio para o que a UI
// entende é EXATAMENTE o tipo de código que se duplica mal e diverge em
// silêncio — foi o que aconteceu antes, com o lean a apontar para um endpoint
// que já não existia e a consola HQ a nem sequer tentar (esperava a resposta
// inteira, até 150 s, com três pontinhos a girar).
//
// A app traz o `Response` (com a sua base e o seu token); daqui sai o fio já
// traduzido. Sem fetch, sem auth, sem framework.
// ─────────────────────────────────────────────────────────────────────────────

export const HEXIS_STREAM_PATH = '/hexis/ai/chat';

/** Chunk normalizado que as UIs consomem. */
export type HexisStreamChunk =
  | { type: 'step'; content: string }        // o que o Hexis está a fazer AGORA
  | { type: 'reply'; content: string }       // um pedaço de resposta (agente)
  | { type: 'done'; payload: HexisReply };   // resposta completa (/hexis/chat)

/** Corpo canónico do POST /hexis/ai/chat. */
export function buildHexisStreamBody(req: {
  message: string;
  currentPage?: string;
  history?: unknown[];
  clientName?: string | null;
  navHistory?: string[];
}): Record<string, unknown> {
  return {
    message: req.message,
    currentPage: req.currentPage || '/home',
    ...(req.history?.length ? { history: req.history } : {}),
    ...(req.clientName ? { clientName: req.clientName } : {}),
    ...(req.navHistory?.length ? { navHistory: req.navHistory } : {}),
  };
}

/**
 * Traduz o fio SSE do servidor para chunks de UI.
 *
 * Detalhes que só se descobrem a ler bytes reais:
 *   • um frame pode chegar PARTIDO ao meio — daí o buffer e o `frames.pop()`;
 *   • o servidor manda comentários `: hb` de keepalive (o prefill em CPU chega
 *     a 30 s antes do 1.º token; sem tráfego, os proxies fecham a ligação).
 *     Comentários não são dados e são ignorados sem ruído;
 *   • `done` fecha o fio: os tokens já compuseram a resposta;
 *   • `error` é do SERVIDOR, e sobe como exceção — quem chama decide o que
 *     mostrar (não é o parser que escreve copy).
 */
export async function* parseHexisSse(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<HexisStreamChunk, void, unknown> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';   // o último frame pode estar incompleto

      for (const frame of frames) {
        const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
        if (!dataLine) continue;                       // `: hb` e afins
        const payload = dataLine.slice(6).trim();
        if (!payload || payload === '[DONE]') continue;

        let ev: { type?: string; token?: string; label?: string; message?: string; payload?: unknown };
        try { ev = JSON.parse(payload); } catch { continue; }

        if (ev.type === 'token' && ev.token) yield { type: 'reply', content: ev.token };
        else if (ev.type === 'step' && ev.label) yield { type: 'step', content: ev.label };
        else if (ev.type === 'error') throw new Error(ev.message || 'Erro no stream do Hexis');
        else if (ev.type === 'done') {
          // O `/hexis/chat` fecha com o objeto COMPLETO (reply, intent,
          // pending_action, sugestões) — é o mesmo payload da versão JSON, e é
          // por isso que ligar o progresso não custou nenhuma funcionalidade.
          // O `/hexis/ai/chat` (agente) fecha sem payload: os tokens já
          // compuseram a resposta.
          if (ev.payload) yield { type: 'done', payload: normalizeHexisReply(ev.payload) };
          return;
        }
      }
    }
  } finally {
    // Sair a meio (o utilizador fechou o painel) tem de largar a ligação — sem
    // isto, cada abertura do copiloto deixaria um stream pendurado no servidor.
    reader.cancel().catch(() => {});
  }
}

/**
 * Chat COM progresso — o mesmo `/hexis/chat`, com `stream: true`.
 *
 * A app injeta o `open` (a sua base, o seu token, o seu fetch); daqui saem os
 * passos em tempo real e, no fim, o payload completo — o mesmo objeto que a
 * versão sem stream devolveria. Uma implementação para as duas apps: era
 * exatamente aqui que o lean e a consola tinham divergido.
 */
export function createHexisChatStream(
  open: (path: string, body: unknown) => Promise<{ ok: boolean; body: ReadableStream<Uint8Array> | null }>,
) {
  return async function* stream(req: HexisChatRequest): AsyncGenerator<HexisStreamChunk, void, unknown> {
    const res = await open(HEXIS_CHAT_PATH, { ...buildHexisChatBody(req), stream: true });
    if (!res.ok || !res.body) throw new Error('Não foi possível abrir o stream do Hexis');
    yield* parseHexisSse(res.body);
  };
}
